import React, { useState } from 'react';
import { Alert, Input, Modal, Select, Typography, message } from 'antd';
import { adminApi } from '@/services/api';
import { apiErrorMessage } from '@/utils/apiError';
import { parsePlayerIdList } from '@/utils/playerIdList';
import { LANGUAGES, LANGUAGE_LABEL, MOMENT_LABEL, sampleBody } from '@/utils/matchPushCopy';
import type { MatchPushPlan, MatchPushSampleResult, PushLanguage } from '@/types/matchPushes';

const { Text } = Typography;
const MAX_TEST_PLAYERS = 10;
const OWN_LANGUAGE = 'own';

interface Props {
  plan: MatchPushPlan | null;
  onClose: () => void;
}

/** Sends one plan's copy to up to 10 test players now; not counted, not capped, switch not required. */
export const SendTestModal: React.FC<Props> = ({ plan, onClose }) => {
  const [text, setText] = useState('');
  const [language, setLanguage] = useState<PushLanguage | typeof OWN_LANGUAGE>(OWN_LANGUAGE);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState<MatchPushSampleResult | null>(null);
  // The ids and language last sent; sending the same again needs a change first.
  const [sentKey, setSentKey] = useState<string | null>(null);

  const parsed = parsePlayerIdList(text);
  const tooMany = parsed.ids.length > MAX_TEST_PLAYERS;
  const key = `${language}:${parsed.ids.join(',')}`;
  const canSend =
    parsed.ids.length > 0 && !tooMany && parsed.invalid.length === 0 && key !== sentKey;

  const close = () => {
    setText('');
    setLanguage(OWN_LANGUAGE);
    setResult(null);
    setSentKey(null);
    onClose();
  };

  const send = async () => {
    if (!plan || !canSend) return;
    setSending(true);
    try {
      const response = await adminApi.matchPushes.sendSample(
        plan.id,
        sampleBody(parsed.ids, language === OWN_LANGUAGE ? undefined : language)
      );
      setResult(response.data);
      setSentKey(key);
      const n = response.data.queued;
      message.success(`${n} test push${n === 1 ? '' : 'es'} queued`);
    } catch (error) {
      message.error(apiErrorMessage(error, 'Failed to send the test'));
    } finally {
      setSending(false);
    }
  };

  const title = plan
    ? `Send test: ${MOMENT_LABEL[plan.moment]}, ${plan.fixture.team1Name} vs ${plan.fixture.team2Name}`
    : '';
  const invalidText = `${parsed.invalid.slice(0, 3).join(', ')}${parsed.invalid.length > 3 ? '...' : ''}`;

  return (
    <Modal
      open={plan !== null}
      title={title}
      okText="Send test"
      okButtonProps={{ disabled: !canSend, loading: sending }}
      onOk={send}
      onCancel={close}
      destroyOnClose
    >
      <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
        Paste up to {MAX_TEST_PLAYERS} player ids. Each gets this push now, in their own language
        (English without a locale) unless you pick one below. It is not counted against the daily
        cap, and the sending switch does not need to be on.
      </Text>
      <Select
        value={language}
        onChange={(value) => {
          setLanguage(value);
          setResult(null);
        }}
        style={{ width: 260, marginBottom: 4 }}
        aria-label="Language"
        options={[
          { value: OWN_LANGUAGE, label: "Each player's own language" },
          ...LANGUAGES.map((l) => ({ value: l, label: LANGUAGE_LABEL[l] })),
        ]}
      />
      <Text type="secondary" style={{ display: 'block', marginBottom: 8, fontSize: 12 }}>
        Pick Hindi or Hinglish to check that copy on your phone.
      </Text>
      <Input.TextArea
        rows={4}
        value={text}
        onChange={(e) => {
          setText(e.target.value);
          setResult(null);
        }}
        placeholder="One player id per line or comma separated"
      />
      {tooMany && (
        <Alert
          style={{ marginTop: 8 }}
          type="error"
          showIcon
          message={`${parsed.ids.length} ids; at most ${MAX_TEST_PLAYERS} per test`}
        />
      )}
      {parsed.invalid.length > 0 && (
        <Alert
          style={{ marginTop: 8 }}
          type="error"
          showIcon
          message={`Not player ids: ${invalidText}`}
        />
      )}
      {result && (
        <Alert
          style={{ marginTop: 8 }}
          type={result.skipped.length ? 'warning' : 'success'}
          showIcon
          message={`${result.queued} queued, ${result.skipped.length} skipped`}
          description={
            result.skipped.length ? (
              <ul style={{ margin: 0, paddingLeft: 18 }}>
                {result.skipped.map((s) => (
                  <li key={s.userId}>
                    {s.userId}: {s.reason}
                  </li>
                ))}
              </ul>
            ) : undefined
          }
        />
      )}
    </Modal>
  );
};
